import { forwardRef, useEffect, useMemo, useRef, useState } from "react";

import { ChevronDown } from "lucide-react";

import { cn } from "@/lib/utils";
import { COUNTRY_DATA } from "@/utils/helpers/countryUtils";

type RegionDisplayNames = {
  of: (code: string) => string | undefined;
};

const regionDisplayNamesCache = new Map<string, RegionDisplayNames | null>();

function getRegionDisplayNames(
  locale: string | undefined
): RegionDisplayNames | null {
  const normalizedLocale = (locale || "en").toLowerCase();

  if (regionDisplayNamesCache.has(normalizedLocale)) {
    return regionDisplayNamesCache.get(normalizedLocale) || null;
  }

  const intlWithDisplayNames = Intl as unknown as {
    DisplayNames?: new (
      locales?: string | string[],
      options?: { type: "region" }
    ) => RegionDisplayNames;
  };

  if (!intlWithDisplayNames.DisplayNames) {
    regionDisplayNamesCache.set(normalizedLocale, null);
    return null;
  }

  try {
    const regionDisplayNames = new intlWithDisplayNames.DisplayNames(
      [normalizedLocale],
      { type: "region" }
    );
    regionDisplayNamesCache.set(normalizedLocale, regionDisplayNames);
    return regionDisplayNames;
  } catch {
    regionDisplayNamesCache.set(normalizedLocale, null);
    return null;
  }
}

function getLocalizedCountryName(
  countryCode: string,
  fallbackName: string,
  locale: string | undefined
): string {
  const regionDisplayNames = getRegionDisplayNames(locale);
  return regionDisplayNames?.of(countryCode.toUpperCase()) || fallbackName;
}

export interface ULThemeCountryCodePickerProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "onChange"> {
  /**
   * Selected country information
   */
  selectedCountry?:
    | {
        name: string;
        code: string; // Country code like "DZ"
        flag: string; // Flag emoji or URL
      }
    | undefined;
  /**
   * Placeholder text when no country is selected
   */
  placeholder?: string;
  /**
   * Loading state
   */
  isLoading?: boolean;
  /**
   * Full width styling
   */
  fullWidth?: boolean;
  /**
   * Callback when country is selected
   */
  onChange?: (countryCode: string) => void;
  /**
   * Locale used to localize country names
   */
  locale?: string;
}

const ULThemeCountryCodePicker = forwardRef<
  HTMLButtonElement,
  ULThemeCountryCodePickerProps
>(
  (
    {
      selectedCountry,
      placeholder = "Select Country",
      isLoading = false,
      fullWidth = false,
      className,
      disabled,
      onChange,
      locale,
      ...rest
    },
    ref
  ) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const dropdownListRef = useRef<HTMLDivElement>(null);
    const searchQueryRef = useRef<string>("");
    const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Build country options with localized names.
    const countries = useMemo(
      () =>
        Object.entries(COUNTRY_DATA)
          .map(([code, data]) => ({
            code,
            name: getLocalizedCountryName(code, data.name, locale),
            flag: data.flag,
          }))
          .sort((a, b) =>
            a.name.localeCompare(b.name, locale || "en", {
              sensitivity: "base",
            })
          ),
      [locale]
    );

    const selectedCountryName = useMemo(() => {
      if (!selectedCountry) {
        return "";
      }

      return getLocalizedCountryName(
        selectedCountry.code,
        selectedCountry.name,
        locale
      );
    }, [selectedCountry, locale]);

    // Close dropdown when clicking outside
    useEffect(() => {
      if (!isOpen) {
        return;
      }

      const handleClickOutside = (event: MouseEvent) => {
        if (
          dropdownRef.current &&
          !dropdownRef.current.contains(event.target as Node)
        ) {
          setIsOpen(false);
          searchQueryRef.current = "";
        }
      };

      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, [isOpen]);

    // Handle keyboard search when dropdown is open
    useEffect(() => {
      if (!isOpen) {
        searchQueryRef.current = "";
        return;
      }

      const handleKeyDown = (event: KeyboardEvent) => {
        // Ignore if user is typing in an input field
        if (
          event.target instanceof HTMLInputElement ||
          event.target instanceof HTMLTextAreaElement
        ) {
          return;
        }

        // Only handle printable characters (letters, numbers, basic punctuation)
        if (event.key.length === 1 && !event.ctrlKey && !event.metaKey) {
          event.preventDefault();

          // Clear previous timeout
          if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
          }

          // Append to search query
          searchQueryRef.current += event.key.toLocaleLowerCase(locale || "en");

          // Find the first country that starts with the search query
          const matchingCountry = countries.find((country) =>
            country.name
              .toLocaleLowerCase(locale || "en")
              .startsWith(searchQueryRef.current)
          );

          if (matchingCountry && dropdownListRef.current) {
            // Find the button element for this country
            const countryButton = dropdownListRef.current.querySelector(
              `[data-country-code="${matchingCountry.code}"]`
            ) as HTMLElement;

            if (countryButton) {
              // Scroll the matching country into view
              countryButton.scrollIntoView({
                behavior: "smooth",
                block: "nearest",
              });

              // Highlight the matched country
              countryButton.focus();
            }
          }

          // Reset search query after 1 second of inactivity
          searchTimeoutRef.current = setTimeout(() => {
            searchQueryRef.current = "";
          }, 1000);
        }
      };

      document.addEventListener("keydown", handleKeyDown);
      return () => {
        document.removeEventListener("keydown", handleKeyDown);
        if (searchTimeoutRef.current) {
          clearTimeout(searchTimeoutRef.current);
        }
      };
    }, [isOpen, countries, locale]);

    // Base styles with theme overrides
    const baseStyles = cn(
      "inline-flex items-center justify-between px-4 py-4 text-left font-medium transition-colors duration-150 ease-in-out",
      "theme-universal:bg-input-bg",
      "theme-universal:text-input-text",
      "theme-universal:border",
      "theme-universal:border-input-border",
      "theme-universal:text-(length:--ul-theme-font-body-text-size)",
      "theme-universal:font-body",
      "theme-universal:rounded-input",
      "theme-universal:hover:border-base-focus",
      "theme-universal:focus:outline-none theme-universal:focus:ring-2 theme-universal:focus:ring-base-focus/20"
    );

    const widthStyles = fullWidth ? "w-full" : "";
    const disabledStyles =
      disabled || isLoading
        ? "disabled:opacity-70 cursor-not-allowed"
        : "cursor-pointer";

    const dropdownStyles = cn(
      "absolute z-50 w-full mt-1 max-h-60 overflow-auto",
      "theme-universal:bg-input-bg",
      "theme-universal:border",
      "theme-universal:border-input-border",
      "theme-universal:rounded-input",
      "shadow-lg"
    );

    const optionStyles = cn(
      "w-full px-4 py-3 text-left transition-colors duration-150 ease-in-out flex items-center",
      "theme-universal:text-input-text",
      "theme-universal:hover:bg-input-border/30",
      "theme-universal:focus:bg-input-border/30",
      "theme-universal:focus:outline-none",
      "cursor-pointer"
    );

    const handleSelect = (countryCode: string) => {
      onChange?.(countryCode);
      setIsOpen(false);
      searchQueryRef.current = "";
    };

    return (
      <div className={cn("relative", className)} ref={dropdownRef}>
        <button
          ref={ref}
          type="button"
          disabled={disabled || isLoading}
          onClick={() => !disabled && !isLoading && setIsOpen(!isOpen)}
          className={cn(baseStyles, widthStyles, disabledStyles)}
          {...rest}
        >
          <div className="flex items-center space-x-3 flex-1 min-w-0">
            {selectedCountry ? (
              <>
                {/* Flag */}
                <div className="flex-shrink-0 w-6 h-4 flex items-center justify-center">
                  {selectedCountry.flag.startsWith("http") ? (
                    <img
                      src={selectedCountry.flag}
                      alt={`${selectedCountryName} flag`}
                      className="w-6 h-4 object-cover rounded-sm"
                    />
                  ) : (
                    <span className="text-lg">{selectedCountry.flag}</span>
                  )}
                </div>

                {/* Country Info */}
                <div className="flex-1 min-w-0">
                  <span className="theme-universal:text-input-text theme-universal:font-body truncate">
                    {selectedCountryName}
                  </span>
                </div>
              </>
            ) : (
              <>
                {/* Placeholder state */}
                <div className="flex-shrink-0 w-6 h-4 theme-universal:bg-input-border rounded-sm"></div>
                <span className="theme-universal:text-input-labels flex-1 truncate">
                  {placeholder}
                </span>
              </>
            )}
          </div>

          {/* Chevron */}
          <div className="flex-shrink-0 ml-2">
            <ChevronDown
              className={cn(
                "w-4 h-4 theme-universal:text-input-labels transition-transform duration-200",
                isLoading && "animate-pulse",
                isOpen && "rotate-180"
              )}
            />
          </div>
        </button>

        {isOpen && (
          <div className={dropdownStyles} ref={dropdownListRef}>
            {countries.map((country) => (
              <button
                key={country.code}
                type="button"
                data-country-code={country.code}
                onClick={() => handleSelect(country.code)}
                className={optionStyles}
              >
                <span className="mr-3 text-lg flex-shrink-0">
                  {country.flag}
                </span>
                <span className="theme-universal:text-input-text flex-1 truncate">
                  {country.name}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }
);

ULThemeCountryCodePicker.displayName = "ULThemeCountryCodePicker";

export default ULThemeCountryCodePicker;
